import React from 'react';

import { createPortal } from 'react-dom';

import { createQueryClient, QueryClientProvider, ReactQueryDevtools } from '@lumapps/base-api/react-query';
import { Provider, Store } from '@lumapps/redux/react';
import { BrowserRouter } from '@lumapps/router';
import { TranslationsProvider, TranslationConfig } from '@lumapps/translations';
import { ErrorBoundary, ErrorBoundaryProps } from '@lumapps/utils/components/ErrorBoundary';

/** Add html attribute on element focus by keyboard (required for LumX focus style). */
import 'focus-visible';

declare const window: any;

const queryClient = createQueryClient();

export interface ShellProps {
    children: React.ReactNode;
    /** base URL for the routing */
    baseUrl: string;
    /** redux store */
    store: Store<BaseStore>;
    /** list of translations */
    translationsState: TranslationConfig;
    /** custom properties for the error boundary */
    errorBoundaryProps?: Partial<ErrorBoundaryProps>;
    /** whether we are on a dev environment or not. */
    isDevEnv?: boolean;
}

/**
 * Component that wraps any component with necessary providers and configurations.
 * @param ShellProps
 * @returns Shell
 */
export const Shell: React.FC<ShellProps> = ({
    baseUrl,
    store,
    children,
    translationsState,
    errorBoundaryProps,
    isDevEnv,
}) => {
    if (window.lumappsPerformances) {
        window.lumappsPerformances.loaderHidden = Date.now();
    }

    return (
        <ErrorBoundary {...errorBoundaryProps}>
            <QueryClientProvider client={queryClient}>
                <Provider store={store}>
                    <TranslationsProvider value={translationsState}>
                        <BrowserRouter basename={baseUrl}>{children}</BrowserRouter>
                    </TranslationsProvider>
                </Provider>

                {/**
                 * ℹ️ Even though Devtools are excluded in production builds (@see https://tanstack.com/query/latest/docs/react/devtools#devtools-in-production)
                 * avoid creating an unnecessary portal in production builds
                 */}
                {isDevEnv && createPortal(<ReactQueryDevtools />, document.body)}
            </QueryClientProvider>
        </ErrorBoundary>
    );
};
